/**
 * lib/ConfigContext.tsx
 * ----------------------
 * What: Fetches remote configuration on startup and exposes `config`, loading
 * status, and connectivity status (offline/server_down).
 * Why: Lets the app toggle features and behaviour remotely without rebuilding.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AppState } from 'react-native';
import { fetchRemoteConfig, localDefaultConfig, RemoteConfig } from '../utils/remoteConfig';

interface ConfigContextValue {
  config: RemoteConfig;
  loading: boolean;
  status: 'ok' | 'offline' | 'server_down';
  refresh: () => Promise<void>;
}

const ConfigContext = createContext<ConfigContextValue>({ config: localDefaultConfig, loading: true, status: 'ok', refresh: async () => {} });

export const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<RemoteConfig>(localDefaultConfig);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<'ok' | 'offline' | 'server_down'>('ok');

  const refreshConfig = useCallback(async (silent: boolean = true) => {
    try {
      if (!silent) setLoading(true);

      // 1. basic offline check
      const isBrowserOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
      let offline = isBrowserOffline;

      // On native/web, NetInfo for robust check
      if (!isBrowserOffline) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const NetInfo = require('@react-native-community/netinfo');
          const state = await NetInfo.fetch();
          offline = !state.isConnected;
        } catch {}
      }

      if (offline) {
        setStatus('offline');
        return;
      }

      const c = await fetchRemoteConfig();
      // Force logout check
      try {
        const stored = typeof window !== 'undefined' && window.localStorage ? Number(window.localStorage.getItem('forceLogoutVersion') || '0') : 0;
        if (c.forceLogoutVersion > stored) {
          // Store new version before logout to prevent loops
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem('forceLogoutVersion', String(c.forceLogoutVersion));
          }
          // Sign out current session
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const { supabase } = require('../lib/supabase');
          await supabase.auth.signOut({ scope: 'local' });
        }
      } catch {}

      setConfig(c);
      setStatus('ok');
    } catch (e) {
      console.warn('[RemoteConfig] Fetch failed:', (e as Error).message);

      // Quick internet reachability probe
      try {
        await fetch('https://www.google.com/generate_204', { method: 'HEAD', mode: 'no-cors' });
        // If we reach here, internet is fine -> server issue
        setStatus('server_down');
      } catch {
        setStatus('offline');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Initial fetch on mount (not silent)
  useEffect(() => {
    refreshConfig(false);
  }, [refreshConfig]);

  // Refetch when the app comes back to the foreground
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        refreshConfig();
      }
    });
    // Also set up periodic polling every 5 minutes
    const intervalId = setInterval(() => {
      refreshConfig();
    }, 5 * 60 * 1000);

    return () => {
      subscription.remove();
      clearInterval(intervalId);
    };
  }, [refreshConfig]);

  return (
    <ConfigContext.Provider value={{ config, loading, status, refresh: refreshConfig }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => useContext(ConfigContext); 